/**
 * Keeps two connectivity checks that overlap in time from writing their answers in the wrong
 * order.
 *
 * Several things ask the app to check whether our server is reachable: the browser's online and
 * offline events, boot, a request that failed somewhere else, coming back to the app from the
 * background, and the Retry button on the offline splash. None of them knows about the others, so
 * two checks can easily be in flight at the same time.
 *
 * That matters because a check is allowed up to REACHABILITY_PROBE_TIMEOUT_MS (10 seconds) before
 * it gives up, while a check against a healthy network answers in a few milliseconds. So a slow
 * check that started first can finish LAST and overwrite the newer, correct answer with its own
 * old one. On Android there is a worse version of the same thing: while the app sits in the
 * background the WebView cannot use the network and its timers are frozen, so a check started
 * before the app went to sleep can report its failure moments after the app has woken up and a
 * fresh check has already found the network working. The app then shows "Offline" on a phone whose
 * network is fine. It does not stay that way for ever — the six-minute foreground tick re-checks an
 * Offline belief (df-eabc7656368c), and so does the next return to the app — but six minutes of
 * showing the wrong thing and not syncing is the cost of letting the stale answer win in the first
 * place.
 *
 * The rule is: a check may write its answer only if no check that started LATER has already
 * written one. Each check takes a number before it starts, and asks for permission with that
 * number when it has an answer.
 *
 * Note what the comparison is against, because the other choice looks equally sensible and is
 * wrong. Permission is refused only by a later check that has already ANSWERED — never by one that
 * is merely still running. If a started-but-unfinished check could block an earlier one, then a
 * check that hangs for its full 10 seconds would freeze the connection state for that whole
 * stretch, and any answer that arrived in the meantime would be thrown away. Dropping an answer
 * because something newer is already known is the fix; dropping one because something newer MIGHT
 * become known later is a different bug.
 *
 * This holds no connection state of its own. It only decides who may write, and the caller does
 * the writing.
 */
export class ProbeResultGate {
  /** The number the next check to start will be given. Counts up and never repeats. */
  private nextProbeNumber = 1;
  /** The highest number that has already been allowed to write. 0 before anything has. */
  private highestWrittenProbeNumber = 0;

  /** Take this check's place in the order. Call it when the check STARTS, not when it answers. */
  startProbe(): number {
    return this.nextProbeNumber++;
  }

  /**
   * Ask whether the check numbered `probeNumber` may write its answer, and record it when the
   * answer is yes.
   *
   * Returns false when a check that started later has already written — this check's answer is old
   * news and the caller must drop it rather than publish it.
   *
   * Permission is asked for and recorded in one call on purpose. Two calls (ask, then record)
   * would let a caller do the first and forget the second, which reads as working code and
   * silently turns the whole gate off.
   */
  claimResult(probeNumber: number): boolean {
    if (probeNumber <= this.highestWrittenProbeNumber) return false;
    this.highestWrittenProbeNumber = probeNumber;
    return true;
  }
}
